import { useLingui } from "@lingui/react";
import { type QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { paths } from "~/config/routes";
import { settleInvalidate } from "~/domain/projectionSettle";
import { useAuth } from "~/features/auth";
import { hrefForCreatedRecord } from "~/features/navigation/recordHome/hrefForCreatedRecord";

import { awaitRunTermination, submitWorkflowRun } from "../api/workflowRuns";
import { humanizeKey } from "../lib/humanize";
import { generateIdempotencyKey } from "../lib/idempotency";
import { createdRecordIdKeys, firstRunWarning, readRunOutputId } from "../lib/runOutput";
import { WorkflowRunTrackerContext } from "./context";
import { createLiveToast, type LiveToast } from "./liveToast";
import type {
  StartRunOptions,
  TrackedRun,
  TrackedRunTarget,
  WorkflowRunTrackerValue,
} from "./types";

/**
 * Far longer than the 60s `submitWorkflowRunAndAwait` allows. That cap protects
 * a caller that is BLOCKED on the result — a spinner nobody wants to watch for
 * a minute. Here nothing is blocked: the dialog is already closed and the user
 * is working, so the tracker's job is to keep reporting for as long as a run
 * plausibly takes. Under a loaded local stack the runner routinely needs
 * minutes, and giving up at 60s would report a healthy run as lost.
 */
const TRACKED_RUN_TIMEOUT_MS = 5 * 60_000;

/**
 * How long a settled pk stays flagged for the row flash.
 *
 * It MUST expire. The collection tables are virtualized, so a row that is
 * scrolled out and back is a fresh mount — an indefinitely-flagged pk would
 * replay its animation every time the user scrolled past it. Long enough to
 * cover the refetch plus the animation, short enough that it cannot outlive the
 * user's attention.
 */
const SETTLED_FLASH_MS = 6000;

/**
 * The collection lists are served by the async search projection, which can
 * trail the run's record writes by several seconds. One invalidation right at
 * success therefore often refetches a list that does NOT contain the created
 * record yet — the row flash and scroll-to-row would then miss their window.
 * So after success the tracker re-invalidates on this cadence until the row
 * shows up in a cached collection page (bounded by the recheck cap).
 */
const CREATED_ROW_SETTLE_MS = 2500;
const CREATED_ROW_MAX_RECHECKS = 4;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** True when any cached collection page under `queryKeys` contains `pk`. */
const collectionCachesHoldPk = (
  queryClient: QueryClient,
  queryKeys: readonly QueryKey[],
  pk: string,
): boolean =>
  queryKeys.some((queryKey) =>
    queryClient.getQueriesData({ queryKey }).some(([, data]) => {
      const items = (data as { items?: Array<{ data?: Record<string, unknown> }> } | undefined)
        ?.items;
      if (!Array.isArray(items)) {
        return false;
      }

      return items.some((item) => String(item.data?.id ?? item.data?.pk ?? "") === pk);
    }),
  );

/** True when any collection query is cached at all under `queryKeys`. */
const hasCollectionCaches = (queryClient: QueryClient, queryKeys: readonly QueryKey[]): boolean =>
  queryKeys.some((queryKey) => queryClient.getQueriesData({ queryKey }).length > 0);

const targetKey = ({ resource, version }: TrackedRunTarget): string => `${resource}/${version}`;

const errorText = (error: unknown): string | undefined =>
  error instanceof Error && error.message.trim() ? error.message : undefined;

const omitKey = (values: Record<string, string>, key: string): Record<string, string> => {
  const next = { ...values };
  delete next[key];

  return next;
};

/**
 * Submits workflow runs and watches them to completion in the background.
 *
 * Exists because the old behaviour — navigate to the run timeline the moment a
 * run is accepted — answered the wrong question. A user who just filled in
 * "create material" wants to see the material, not a status page for the
 * machinery that made it. So: submit, close, keep working; the tracker reports
 * progress in a toast and flags the settled row so the collection can flash it.
 *
 * State lives here rather than in the dialog because the dialog UNMOUNTS on
 * submit — there would be nothing left to own the poll — and because a run must
 * survive the user navigating elsewhere while it finishes.
 */
export const WorkflowRunTrackerProvider = ({ children }: { children: ReactNode }) => {
  const [runs, setRuns] = useState<TrackedRun[]>([]);
  const [settledPks, setSettledPks] = useState<Record<string, string>>({});

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const { i18n } = useLingui();

  const patchRun = useCallback((key: string, patch: Partial<TrackedRun>) => {
    setRuns((prev) => prev.map((run) => (run.key === key ? { ...run, ...patch } : run)));
  }, []);

  /**
   * The toast's action, and the ONLY confirmation guaranteed to reach the user.
   *
   * The row flash only fires when the created row happens to be in the data the
   * table currently holds — it will not be if the row sorts onto another page,
   * falls outside the active filter, or the user has walked off to a different
   * screen entirely. So the toast offers the record itself whenever the run
   * named one, and falls back to the run timeline when it did not.
   */
  const toastAction = useCallback(
    (runId: string | undefined, target: TrackedRunTarget | undefined, createdPk?: string) => {
      if (createdPk && target) {
        return {
          label: i18n._({ id: "workflowRun.tracker.open", message: "Open" }),
          onClick: () => {
            void hrefForCreatedRecord(queryClient, {
              tenantId,
              schema: target.resource,
              version: target.version,
              objectId: createdPk,
            }).then((href) => {
              void navigate(href);
            });
          },
        };
      }

      return runId
        ? {
            label: i18n._({ id: "workflowRun.tracker.viewRun", message: "View run" }),
            onClick: () => void navigate(paths.workflowRunDetail(runId)),
          }
        : undefined;
    },
    [i18n, navigate, queryClient, tenantId],
  );

  const watchRun = useCallback(
    async (
      run: { key: string; runId: string; definitionKey: string; title: string; live: LiveToast },
      options: Pick<StartRunOptions, "target" | "invalidateKeys" | "createdResource">,
    ) => {
      const { key, runId, definitionKey, title, live } = run;
      const { target, invalidateKeys, createdResource } = options;
      const recordTarget = createdResource ?? target;

      try {
        const terminal = await awaitRunTermination({
          runId,
          definitionKey,
          timeoutMs: TRACKED_RUN_TIMEOUT_MS,
        });

        const createdPk = recordTarget
          ? readRunOutputId(terminal.output, createdRecordIdKeys(recordTarget.resource))
          : undefined;

        const keysToInvalidate = invalidateKeys ?? [];

        if (createdPk) {
          // Refresh BEFORE announcing success: the toast is the user's cue to
          // look at the table, and it should not arrive while the table still
          // shows the pre-run data.
          await Promise.all(
            keysToInvalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
          );

          // The projection may still be behind — keep re-invalidating until the
          // created row is actually in a cached page, so the flash and the
          // scroll-to-row fire against data that contains it. Skipped when the
          // user has no collection page mounted (nothing to flash there).
          if (hasCollectionCaches(queryClient, keysToInvalidate)) {
            for (
              let attempt = 0;
              attempt < CREATED_ROW_MAX_RECHECKS &&
              !collectionCachesHoldPk(queryClient, keysToInvalidate, createdPk);
              attempt += 1
            ) {
              await sleep(CREATED_ROW_SETTLE_MS);
              await Promise.all(
                keysToInvalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
              );
            }
          }
        } else {
          // No created pk means this run EDITED existing records (the
          // EditRecordDialog path) — there is no converge loop watching for a
          // new row, so an immediate refetch against the still-lagging search
          // projection would answer with the PRE-edit state and visibly revert
          // what the run just wrote. Postpone the refetch past the settle
          // window instead.
          for (const queryKey of keysToInvalidate) {
            settleInvalidate(queryClient, queryKey);
          }
        }

        patchRun(key, { state: "succeeded", createdPk });

        if (recordTarget && createdPk) {
          // Not `key` — that is the run's own key, still in scope here.
          const flashKey = targetKey(recordTarget);

          setSettledPks((prev) => ({ ...prev, [flashKey]: createdPk }));
          // Self-expiring so a virtualized row cannot replay its flash on every
          // scroll-back. Cleared by pk, so a later run for the same collection
          // that has already replaced this flag is left alone.
          setTimeout(() => {
            setSettledPks((prev) =>
              prev[flashKey] === createdPk ? omitKey(prev, flashKey) : prev,
            );
          }, SETTLED_FLASH_MS);
        }

        // A SUCCEEDED run can still carry a warning in its result (a partial
        // failure, a skipped step). The toast is often the only outcome surface
        // the user sees — the run page shows the full set, the toast names the
        // first one.
        const warning = firstRunWarning(terminal.output);

        live.success(title, {
          description: warning
            ? i18n._({
                id: "workflowRun.tracker.doneWithWarning",
                message: "Finished — {warning}",
                values: { warning },
              })
            : i18n._({ id: "workflowRun.tracker.done", message: "Finished" }),
          action: toastAction(runId, recordTarget, createdPk),
        });
      } catch (error) {
        const message = errorText(error);

        patchRun(key, { state: "failed", error: message });

        live.error(title, {
          description:
            message ?? i18n._({ id: "workflowRun.tracker.failed", message: "The run failed." }),
          action: toastAction(runId, undefined),
        });
      }
    },
    [i18n, patchRun, queryClient, toastAction],
  );

  const startRun = useCallback(
    async ({
      definitionKey,
      definitionVersion,
      input,
      idempotencyKey,
      title,
      target,
      createdResource,
      correlationId,
      createResume,
      invalidateKeys,
    }: StartRunOptions): Promise<string | undefined> => {
      const key = generateIdempotencyKey();
      const label = title ?? humanizeKey(definitionKey);
      const live = createLiveToast(`workflow-run-${key}`);

      setRuns((prev) => [...prev, { key, definitionKey, title: label, state: "pending", target }]);

      live.loading(label, {
        description: i18n._({ id: "workflowRun.tracker.running", message: "Running…" }),
      });

      let runId: string | undefined;

      try {
        // DIRECT carries the correlation id without an action key. RECORD_ACTION
        // is rejected unless actionKey is set ("context.trigger is incomplete").
        const context = correlationId
          ? { trigger: { type: "DIRECT" as const }, correlationId }
          : undefined;

        if (correlationId) {
          console.warn("[create-completion] submit", {
            definitionKey,
            correlationId,
            trigger: "DIRECT",
          });
        }

        const submitted = await submitWorkflowRun(
          definitionKey,
          input,
          idempotencyKey ?? key,
          definitionVersion,
          context,
          undefined,
          correlationId ? createResume : undefined,
        );
        runId = submitted.runId;
      } catch (error) {
        const message = errorText(error);

        patchRun(key, { state: "failed", error: message });
        live.error(label, {
          description:
            message ??
            i18n._({ id: "workflowRun.tracker.submitFailed", message: "Could not start the run." }),
        });

        return undefined;
      }

      if (!runId) {
        patchRun(key, { state: "failed" });
        live.error(label, {
          description: i18n._({
            id: "workflowRun.tracker.noRunId",
            message: "The workflow did not return a run id.",
          }),
        });

        return undefined;
      }

      patchRun(key, { runId });

      // Deliberately NOT awaited: `startRun` resolves at SUBMITTED so the caller
      // can close its dialog now. The watch keeps going on its own.
      void watchRun(
        { key, runId, definitionKey, title: label, live },
        { target, invalidateKeys, createdResource },
      );

      return runId;
    },
    [i18n, patchRun, watchRun],
  );

  const settledPkFor = useCallback(
    (target: TrackedRunTarget): string | undefined => settledPks[targetKey(target)],
    [settledPks],
  );

  const clearSettled = useCallback((target: TrackedRunTarget) => {
    setSettledPks((prev) => omitKey(prev, targetKey(target)));
  }, []);

  const value = useMemo<WorkflowRunTrackerValue>(
    () => ({ runs, startRun, settledPkFor, clearSettled }),
    [clearSettled, runs, settledPkFor, startRun],
  );

  return (
    <WorkflowRunTrackerContext.Provider value={value}>
      {children}
    </WorkflowRunTrackerContext.Provider>
  );
};
